import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  memo,
  useMemo,
  useCallback,
} from "react";
// import * as PIXI from "pixi.js"; // Not needed anymore

import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectAutoPlaySpeed,
  selectBet,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
  selectFreeSpinsActive,
} from "../../../State/selectors";
import ReelControllerV2 from "./ReelControllerV2"; // New controller
import {
  maskReelForMystery,
  resolveReelForMystery,
} from "../../Shared/mysteryStaging";
import { goldenShownBoard } from "../../Shared/goldenMysteryStaging";
import { goldenCellTexture } from "../Mystery/goldenMysteryConfig";
import { toStaticCell } from "../GambleScatter/gambleScatterConfig";
import { safeInit } from "../../../utilities";
import { Settings } from "../../../settings";
import DimReelOverlay from "../DimReelOverlay/DimReelOverlay";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import {
  COIN_SYMBOL_IDS,
  MYSTERY_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to skip spin complete delay for trigger rounds.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

const Reel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
        sharedSymbolsContainerRef,
        reelXOffset,
      },
      ref,
    ) => {
      const dispatch = useDispatch();
      const reelController = useRef(null);
      const dimOverlayRef = useRef(null);
      const currentRound = useSelector(selectCurrentRound);
      // The room owning the screen — see the settled-cell repaint below.
      const freeSpinsActive = useSelector(selectFreeSpinsActive);
      const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

      // Use free rounds bet when active, otherwise use regular bet - memoized
      const freeRoundsActive = useSelector(selectFreeRoundsActive);
      const freeRoundsBet = useSelector(selectFreeRoundsBet);
      const regularBet = useSelector(selectBet);
      const bet = useMemo(
        () =>
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
          freeRoundsActive &&
          freeRoundsBet
            ? freeRoundsBet
            : regularBet,
        [freeRoundsActive, freeRoundsBet, regularBet],
      );

      // Landing animations hook
      const { triggerReelLandingAnimations, waitForReelLandings } =
        useReelLandingAnimations();

      // ── Put a settled reveal back after a reload ──
      //
      // Called from TWO places, and it has to be: the paint needs both the
      // round and the sprites, and which of those arrives second is not fixed.
      // The controller paints its own sprites in its constructor from the
      // `reel` prop, so a cold reload can build the board before this
      // component's round effect has ever run.
      //
      // Idempotent — it writes the same texture over the same cell.
      const paintSettledReveal = useCallback(() => {
        const controller = reelController.current;

        if (!controller) return;
        const settledPackage = currentRound?.settledGambleScatter;

        // ── A settled board's Golden cells: the finished composites ──
        //
        // The same photograph the room paints for its arrival board
        // (FreeSpinBasicReel, the isTriggerBoard block): every golden cell as
        // it ended — the coin with its value, the magnet with its total, a
        // scatter already open. x1, because this board paid in the base game.
        // The gamble scatter's cell is skipped: the block below owns it.
        if (currentRound?.boardSettled) {
          const scatterPos = settledPackage?.position;
          for (const cell of goldenShownBoard(currentRound, 1).values()) {
            if (cell.reel !== index) continue;
            if (
              Array.isArray(scatterPos) &&
              scatterPos[0] === cell.reel &&
              scatterPos[1] === cell.row
            ) {
              continue;
            }
            const revealedCell =
              cell.symbolId === SCATTER_3_SYMBOL_ID ||
              cell.symbolId === SCATTER_2_SYMBOL_ID
                ? { ...cell, revealed: true }
                : cell;
            const texture = goldenCellTexture(revealedCell);
            if (!texture) continue;
            controller.setSymbolTextureOnly?.(cell.row, MYSTERY_SYMBOL_ID, texture);
            controller.showSymbolTexture?.(cell.row);
          }
        }

        // The board above is symbol ids, and a settled gamble scatter is not
        // one: the cell is an open gold window with the awarded package inside
        // it, and rebuilt from its id alone it comes back as a CLOSED door —
        // which is what the player found when they came back from the bonus and
        // reloaded.
        //
        // The live path paints this from the reveal itself (see the
        // setSymbolTextureOnly call in useWinController's reveal block). This is
        // the same picture, from the same builder, for the boards no spin
        // produced — the free-spins room already does exactly this for its own
        // arrival board, in FreeSpinBasicReel.
        //
        // GUARDED BY THE FIELD ITSELF. `settledGambleScatter` is written in one
        // place — the reload strip in PopiPlay/RGS, in the branch that has
        // already proved the server owes no feature. So a LIVE trigger cannot
        // carry it, and the award can never be painted before the doors open.
        //
        // This used to key on an `isRestoredBoard` flag that nothing ever set,
        // so it returned here every time and was dead code from the day it was
        // written. A field only the restore can produce cannot go the same way.
        //
        // TEXTURE ONLY: the cell's identity stays the gamble scatter, because
        // the win cycle reads currentSymbolId to pick which spine celebrates.
        const settled = settledPackage;
        if (!Array.isArray(settled?.position) || settled.position[0] !== index) {
          return;
        }

        // Pips LIT, no override. The base game is where the package is won;
        // the flourish that spends the pips happens in the room, and that
        // board is painted by FreeSpinBasicReel with its own pip state.
        const cell = toStaticCell({ tier: settled.tier, value: settled.value });
        if (!cell) return;

        const row = settled.position[1];
        controller.setSymbolTextureOnly?.(row, cell.symbolId, cell.texture);

        // ── AND MADE VISIBLE, which is the half that was missing ──
        //
        // `setSymbolTextureOnly` deliberately does not touch alpha. A landed
        // scatter's flat texture is held at 0 while an OVERLAY SPINE owns the
        // cell, and on a reload that spine never exists — so the composite was
        // being written, correctly and at the right size, onto an invisible
        // sprite. Measured on the 2026-09-16 attempt, which is why that one was
        // abandoned believing the texture was at fault. It was not.
        //
        // Nothing owns this cell on a restored board, so the reel takes it back.
        controller.showSymbolTexture?.(row);

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound, index]);


      // 🎮 Initialize controller once
      useEffect(() => {
        let rafId = null;

        const tryInit = () => {
          rafId = null;
          if (reelController.current) return;

          const sharedContainer = sharedSymbolsContainerRef?.current;
          if (!sharedContainer) {
            rafId = requestAnimationFrame(tryInit);
            return;
          }

          // ── The FIRST paint must already be MASKED ──
          //
          // `reel` is currentRound.reels[index], and the board the server sends
          // is the board AFTER the reveal — so painting it raw shows the answer
          // before the doors have opened. Live that frame is gone immediately;
          // on a RELOAD it sits on screen through the whole of scene building,
          // which in the room measured at nearly a second. The free-spins reel
          // had the same fault and the same fix.
          //
          // Masked against the same round the effect below uses, so the two
          // cannot disagree about which cells are hidden. No feature means the
          // reels come back untouched.
          // A SETTLED board (the trigger round behind a restored room) is the
          // opposite case: its lids opened before the bonus, so it paints what
          // they became. See boardSettled in PopiPlay/RGS.
          const initialReels = currentRound?.boardSettled
            ? resolveReelForMystery(reel, currentRound, index)
            : maskReelForMystery(reel, currentRound, index);

          safeInit(() => {
            reelController.current = new ReelControllerV2(
              dispatch,
              sharedContainer,
              symbolsSpriteSheet,
              symbolsSpriteSheetBlurred,
              initialReels,
              index, // reelIndex
              symbolWidth,
              symbolHeight,
              triggerReelLandingAnimations, // Callback for landing animations
              autoPlaySpeed, // Current autoplay speed
              bet, // Current bet value
              reelXOffset, // Column x, stamped on each sprite
            );
            // ── The landing is part of the spin ──
            //
            // Lets the controller hold `setSpinComplete` until the bounces
            // have finished, so nothing downstream — the spin button, the win
            // presentation, autoplay — is told the round is over while the
            // board is still moving. Set as a property rather than a
            // constructor argument, the same way onReelLanded is.
            //
            // Only the LAST reel ever calls it, and it asks about the whole
            // board, so giving every reel the same function costs nothing.
            reelController.current.waitForBoardSettle = waitForReelLandings;

            // ── INSIDE safeInit, and that is not tidiness ──
            //
            // The sprites are painted in the constructor, so anything the board
            // cannot express as a symbol id goes on now — see
            // paintSettledReveal. It used to be called just after this block,
            // but `safeInit` defers to requestAnimationFrame: the controller was
            // still a frame away and the paint returned at `if (!controller)`
            // every single time. The `currentRound` effect is no second chance
            // either, because on a reload the round never changes again.
            paintSettledReveal();
          });

        };

        tryInit();

        return () => {
          if (rafId !== null) cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // ── The settled cell gets a SECOND chance, when the room gives the
      //    screen back ──
      //
      // This paint has one shot: reel init, and a `currentRound` change that on
      // a reload never comes. Everything that happens between then and the
      // player actually seeing the base board — the room taking the screen, the
      // outro, the wipe, snapClearAll's teardown — runs afterwards, and any of
      // it that repaints the cell from its identity wins. That is why two
      // earlier attempts at this measured a correct paint and still showed a
      // closed door.
      //
      // `freeSpinsActive` going false IS the board coming back — it is the same
      // flag useWinController calls `bonusBoardActive`. Re-asserting here costs
      // nothing when there is no settled package, and the paint is idempotent:
      // it writes the same texture over the same cell.
      useEffect(() => {
        if (freeSpinsActive) return;
        if (!currentRound?.settledGambleScatter && !currentRound?.boardSettled) {
          return;
        }
        paintSettledReveal();
      }, [freeSpinsActive, currentRound, paintSettledReveal]);

      // ↔️ Column position — reelSpacing changes with the viewport, and the
      // offset now lives on the sprites rather than on a parent container.
      useEffect(() => {
        reelController.current?.setReelXOffset?.(reelXOffset);
      }, [reelXOffset]);

      // Update reels when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;

        // ── Stage the mystery cells ──
        //
        // The board the server sends is the board AFTER the reveal: the
        // mysteries are already replaced in `screen`, and where they dropped is
        // reported separately in storage.mystery_feature. So the pre-reveal
        // state — the one the player has to watch land, bounce and build —
        // exists nowhere until it is put back here.
        //
        // DISPLAY ONLY. currentRound in Redux keeps the resolved board, because
        // useWinController reads it to pick which spine celebrates. The cell is
        // handed its real symbol back when the reveal opens, by identity as
        // well as texture — see the orchestration in useWinController.
        const reelData = currentRound?.boardSettled
          ? resolveReelForMystery(currentRound?.reels?.[index], currentRound, index)
          : maskReelForMystery(currentRound?.reels?.[index], currentRound, index);
        reelController.current.updateReels(reelData);

        paintSettledReveal();

        // Update win state and trigger state for last reel only
        if (index === Settings.REEL_COUNT - 1) {
          // Update win state for sound selection
          reelController.current.setHasWinThisSpin?.(currentRound?.win > 0);

          // Update Hold & Win trigger state to skip spin complete delay
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          reelController.current.setIsHoldAndWinTrigger?.(isHoldAndWinTrigger);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound]);

      // 📏 Resize handler
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.resizeSymbol(symbolWidth, symbolHeight);
      }, [symbolWidth, symbolHeight]);

      // ⚡ Auto play speed update
      useEffect(() => {
        if (
          reelController.current &&
          reelController.current.autoPlaySpeed !== autoPlaySpeed
        ) {
          reelController.current.autoPlaySpeed = autoPlaySpeed;
        }
      }, [autoPlaySpeed]);

      // 💰 Bet update - update coin values when bet changes
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.updateBet(bet);
      }, [bet]);

      // 🧹 Cleanup
      useEffect(() => {
        return () => {
          if (!reelController.current) return;

          // Clear all animations, timeouts, and PIXI Ticker callbacks
          reelController.current.clearAllTimeouts();

          // Destroy all spinning sprites and their PIXI objects (textures, containers)
          reelController.current._cleanupSpinningSprites();

          // Reset controller state on unmount
          reelController.current.isStopping = false;
          reelController.current.hasStopped = true;
          reelController.current._spinElapsed = 0;
        };
      }, []);

      // Memoize controller methods
      const startSpin = useCallback(() => {
        reelController.current?.spin();
      }, []);

      const startSpinNoWindUp = useCallback(() => {
        reelController.current?.spinNoWindUp();
      }, []);

      const stopSpin = useCallback(() => {
        reelController.current?.stopSpin();
      }, []);

      const startTurboSpin = useCallback(() => {
        reelController.current?.spinTurbo();
      }, []);

      const stopTurboSpin = useCallback(() => {
        reelController.current?.stopSpinTurbo();
      }, []);

      const startTensionSpin = useCallback(() => {
        reelController.current?.spinTension();
      }, []);

      const stopTensionSpin = useCallback(() => {
        reelController.current?.stopSpinTension();
      }, []);

      const freezeSpin = useCallback(() => {
        reelController.current?.freezeSpin();
      }, []);

      const restoreToIdleState = useCallback((symbolIds, betValue = null) => {
        reelController.current?.restoreToIdleState(symbolIds, betValue);
      }, []);

      const setOnReelLanded = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onReelLanded = callback;
        }
      }, []);

      const setOnLandingPeak = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onLandingPeak = callback;
        }
      }, []);

      const getReelController = useCallback(() => {
        return reelController.current;
      }, []);

      const getReelSpinComplete = useCallback(() => {
        return reelController.current?.hasStopped ?? false;
      }, []);

      const replaceVisibleSymbols = useCallback(
        (targetSymbolId, hideTextures = true) => {
          reelController.current?.replaceVisibleSymbols(
            targetSymbolId,
            hideTextures,
          );
        },
        [],
      );

      const replaceSymbolAtPosition = useCallback(
        (symbolIndex, targetSymbolId) => {
          reelController.current?.replaceSymbolAtPosition(
            symbolIndex,
            targetSymbolId,
          );
        },
        [],
      );

      const dimReel = useCallback(() => {
        dimOverlayRef.current?.dimReel();
      }, []);

      const undimReel = useCallback(() => {
        dimOverlayRef.current?.undimReel();
      }, []);

      // 🎛️ Expose imperative API for external reel control
      useImperativeHandle(
        ref,
        () => ({
          startSpin,
          startSpinNoWindUp,
          stopSpin,

          // Turbo mode (instant drop, no wind-up)
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,

          // Freeze spin instantly (for error popups)
          freezeSpin,

          // Restore reel to idle state with specific symbols (for error recovery)
          restoreToIdleState,

          // Set callback for when reel has fully landed (bounce complete)
          setOnReelLanded,

          // Set callback for overshoot peak (lowest visual point, before bounce-back)
          setOnLandingPeak,

          getReelController,
          getReelSpinComplete,

          // Expanding wild support
          replaceVisibleSymbols,
          replaceSymbolAtPosition,

          // Dim control methods for tension mode and other effects
          dimReel,
          undimReel,
        }),
        [
          startSpin,
          startSpinNoWindUp,
          stopSpin,
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,
          freezeSpin,
          restoreToIdleState,
          setOnReelLanded,
          setOnLandingPeak,
          getReelController,
          getReelSpinComplete,
          replaceVisibleSymbols,
          replaceSymbolAtPosition,
          dimReel,
          undimReel,
        ],
      );

      // Sprites are parented into ReelSet's single shared container so all 5
      // reels' symbols are siblings and can sort by z-index. Nothing is
      // rendered here but the tension dim overlay.
      return (
        <>
          {FEATURES_CONFIG.TENSION.ENABLED && (
            <DimReelOverlay
              ref={dimOverlayRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
            />
          )}
        </>
      );
    },
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index &&
    // Sprites carry their own column x now, so a reelSpacing change must
    // re-render to re-run the setReelXOffset effect. reelSpacing can move
    // without symbolWidth (it also depends on dividerDimensions), which would
    // otherwise leave the columns misaligned from the dividers and overlay.
    prev.reelXOffset === next.reelXOffset,
  // Note: currentRound changes are handled by useEffect, not memo
);

export default Reel;
